import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { field } from '../create/Field';
import { IconChecklist, IconTrash } from '../icons';
import { UnresolvedState, shownState } from '../ui/QueryStates';

type Progress = {
  id: string;
  name: string;
  visits: { visitId: string; total: number; done: number; requiredOpen: number }[];
};
type Visit = { id: string; scheduledStartAt: string | Date | null; status: string };

/**
 * Checklists on a job (PRD v2 §2). The office attaches a form once; every stop on the job
 * gets its own copy, including stops scheduled later. What's shown back is per-visit
 * progress — a job-level "3 of 5" would average away the stop that skipped a safety step.
 */
export function JobChecklistCard({
  jobId,
  checklists,
  visits,
}: {
  jobId: string;
  checklists: Progress[];
  visits: Visit[];
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const templates = useQuery(trpc.checklist.list.queryOptions());
  const [picking, setPicking] = useState(false);
  const [templateId, setTemplateId] = useState('');

  const refresh = () => {
    void qc.invalidateQueries();
    setPicking(false);
    setTemplateId('');
  };
  const attach = useMutation(
    trpc.checklist.attach.mutationOptions({ onSuccess: refresh }),
  );
  const detach = useMutation(
    trpc.checklist.detach.mutationOptions({ onSuccess: refresh }),
  );

  const attachedIds = new Set(checklists.map((c) => c.name));
  const templatesState = shownState(templates, templates.data);
  const available = (templates.data ?? []).filter((t) => !attachedIds.has(t.name));
  const visitLabel = (visitId: string) => {
    const v = visits.find((x) => x.id === visitId);
    if (!v?.scheduledStartAt) return 'Unscheduled stop';
    return new Date(v.scheduledStartAt).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  };
  // Progress rows arrive grouped, not ordered. The Visits card directly above is in
  // schedule order, and the same two stops listed in two different orders on one page
  // reads as a bug — so this card borrows that order rather than inventing its own.
  const visitRank = new Map(visits.map((v, i) => [v.id, i]));
  const inScheduleOrder = <T extends { visitId: string }>(rows: T[]) =>
    rows
      .slice()
      .sort(
        (a, b) =>
          (visitRank.get(a.visitId) ?? Number.MAX_SAFE_INTEGER) -
          (visitRank.get(b.visitId) ?? Number.MAX_SAFE_INTEGER),
      );

  return (
    <SectionCard title={`Checklists (${checklists.length})`}>
      {attach.error ? <Notice tone="danger">{attach.error.message}</Notice> : null}
      {detach.error ? <Notice tone="danger">{detach.error.message}</Notice> : null}

      {checklists.length === 0 ? (
        <p className="mb-3 text-sm text-muted-foreground">
          None on this job. A checklist rides along to every stop, and steps marked
          required stop a tech finishing without them.
        </p>
      ) : (
        <ul className="mb-3 divide-y divide-border text-sm">
          {checklists.map((c) => (
            <li key={c.id} className="py-2 first:pt-0">
              <div className="flex items-center gap-2">
                <p className="min-w-0 flex-1 truncate font-medium">{c.name}</p>
                <button
                  type="button"
                  aria-label={`Remove ${c.name} from this job`}
                  onClick={() => detach.mutate({ jobChecklistId: c.id, ...idem() })}
                  disabled={detach.isPending}
                  className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)]"
                >
                  <IconTrash size={14} />
                </button>
              </div>
              {c.visits.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Waiting on a visit — the first stop scheduled gets a copy.
                </p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {inScheduleOrder(c.visits).map((v) => (
                    <li key={v.visitId} className="flex items-center gap-2 text-xs">
                      <span className="w-20 shrink-0 text-muted-foreground">
                        {visitLabel(v.visitId)}
                      </span>
                      <span className="tabular-nums">
                        {v.done} of {v.total}
                      </span>
                      {v.requiredOpen > 0 ? (
                        <StatusBadge tone="warn">
                          {v.requiredOpen} required left
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="success">All required done</StatusBadge>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {picking ? (
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={`${field} max-w-xs flex-1`}
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            aria-label="Checklist to add"
            autoFocus
          >
            <option value="">Pick a checklist…</option>
            {available.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.items.length} step{t.items.length === 1 ? '' : 's'})
              </option>
            ))}
          </select>
          <Button
            disabled={!templateId || attach.isPending}
            onClick={() =>
              attach.mutate({ jobId, checklistTemplateId: templateId, ...idem() })
            }
          >
            {attach.isPending ? 'Adding…' : 'Add'}
          </Button>
          <Button variant="ghost" onClick={() => setPicking(false)}>
            Cancel
          </Button>
        </div>
      ) : templatesState !== 'ready' ? (
        // `isPending` was handled and `isError` was not, so a failed template list said
        // "No checklists built yet — make one in Settings" to a shop that had built twelve.
        <UnresolvedState
          state={templatesState}
          what="your checklists"
          icon={IconChecklist}
          error={templates.error}
          onRetry={() => void templates.refetch()}
          skeleton="h-8"
        />
      ) : available.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {templates.data && templates.data.length > 0
            ? 'Every checklist you have is already on this job.'
            : 'No checklists built yet — make one in Settings.'}
        </p>
      ) : (
        <Button variant="ghost" onClick={() => setPicking(true)}>
          Add a checklist
        </Button>
      )}
    </SectionCard>
  );
}
